import React, { useState, useEffect, useRef } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Dropdown, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import TagManager from 'react-gtm-module'
import activeFolderIcon from '../../../../_metronic/assets/images/svg/data-room/blue-folder-icon.svg'
import {
  checkRole,
  createAccess,
  createActivityLog,
  deleteFile,
  getRecentUploadApi,
  getViewAndDownloadFile,
} from '../core/_requests'
import { useAuth } from '../../auth'
import { getFileType } from '../../../core/_util'
import { useTimeZone } from '../../../hooks/useTimeZone'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { useNavigate } from 'react-router-dom'
import { NoDataPage } from '../../resources/components/NoDataPage'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import { getLocation } from '../../onboarding/core/_requests'
import { toast } from 'react-toastify'
import { DeleteClassificationModal } from './RecordsClassifications/DeleteClassificationModal'
import { RecordKeeperCreateLinkDrawer } from './RecordsAndFiles/RecordKeeperCreateLinkDrawer'
import ViewDocModal from '../../stakeholders/stakeholders-and-investors/components/individual/view-doc-modal'
import { RenameFileOrFolderModal } from './RenameFileOrFolderModal'
import { RequestFileStatusModal } from './RequestFileStatusModal'
import { RequestAccessModal } from './RequestAccessModal'
import { RecordShareDrawer } from './RecordShareDrawer'
import { PageTitle } from '../../../../_metronic/layout/core'
import { RecordsKeeperAIOverviewBreadCrumbs } from '../core/_constants'
// import { GTMEvent } from '../../../hooks/gtmHelpers'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

export const RecentUploadDataTable = () => {
  const { formatMessage } = useIntl()
  const { companyId, currentUser } = useAuth()
  const { getTimeZoneValue } = useTimeZone()
  const navigate = useNavigate()
  const { getDateValue } = useDateFormat()
  const [recentUpload, setRecentUpload] = useState(false) //eslint-disable-line
  const [shareholders, setShareholders] = useState<any[]>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [showKebabMenu, setShowKebabMenu] = useState(false) //eslint-disable-line
  const [showRenameModal, showRenameShowModal] = useState(false) //eslint-disable-line
  const [showAccessModal, setShowAccessModal] = useState<boolean>(false) //eslint-disable-line
  const [showFileStatusModal, setFileStatusModal] = useState<boolean>(false) //eslint-disable-line
  const [recordId, setRecordId] = useState('')
  const [createLinkDrawer, setCreateLinkDrawer] = useState(false)
  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState<boolean>(false)
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
  const [isActive, setIsActive] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [action, setAction] = useState('') //eslint-disable-line
  const [email, setEmail] = useState('') //eslint-disable-line
  const [fileName, setFileName] = useState()
  const [versionId, setVersionId] = useState()
  const [folderOrFileName, setFolderOrFileName] = useState()
  const [renameUpdate, setRenameUpdate] = useState(false)
  const [viewableData, setViewableData] = useState<
    { downloadUrl: string; fileName: string; extension: string } | {}
  >({})
  const [roleCheckValidatorLoader, setRoleCheckValidatorLoader] = useState<boolean>(false) //eslint-disable-line
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [isOpenShareDrawer, setIsOpenShareDrawer] = useState(false)
  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false)
    }
  }

  const getDeviceType = () => {
    const ua = navigator.userAgent

    if (
      /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera Mobi|Blazer|Dolfin|Dolphin|Skyfire|Zune/.test(
        ua
      )
    ) {
      return 'MOBILE'
    }
    return 'WEB'
  }

  const getOS = () => {
    const ua = navigator.userAgent
    if (/iPhone|iPad|iPod|Macintosh/.test(ua)) return 'APPLE'
    if (/Win/.test(ua)) return 'WINDOWS'
    if (/android/i.test(ua)) return 'ANDROID'
    return 'Unknown'
  }

  const createAccessLog = async (recordId: any) => {
    const os = getOS()
    const deviceTypeView = getDeviceType()
    const locationApiData = await getLocation()
    const city = locationApiData?.data.country_name
    const ipAddress = locationApiData?.data?.ip

    try {
      if (deviceTypeView && os && city && recordId && companyId && currentUser?.userId) {
        const {
          data: { success },
        } = await createAccess(
          recordId,
          os,
          deviceTypeView,
          city,
          ipAddress,
          'CREATE',
          companyId,
          currentUser?.userId
        )
        if (success) {
        }
      }
    } catch (err) {
      console.log(err)
    }
  }

  const createActivity = async (recordId: any) => {
    const os = getOS()
    const deviceTypeView = getDeviceType()
    const locationApiData = await getLocation()
    const city = locationApiData?.data.country_name
    const ipAddress = locationApiData?.data?.ip
    try {
      if (deviceTypeView && os && city && recordId && companyId) {
        const {
          data: { success },
        } = await createActivityLog(
          companyId,
          recordId,
          os,
          deviceTypeView,
          city,
          ipAddress,
          'VIEWED'
        )
        if (success) {
        }
      }
    } catch (err) {
      console.log(err)
    }
  }

  const handleViewFile = async (fileVersionId: string, recordId: string) => {
    if (!companyId) return
    setIsActive(true)
    setIsLoading(true)
    try {
      const {
        data: { data, success },
      } = await getViewAndDownloadFile(fileVersionId, companyId)
      if (success) {
        setViewableData(data)
        createActivity(recordId)
      }
    } catch (error) {
      toast.error('Failed to download document')
    } finally {
      setIsLoading(false)
    }
  }
  const checkRoleValidate = async (id: any, action: string) => {
    setRoleCheckValidatorLoader(true)

    if (!companyId || !id) return
    try {
      const {
        data: { success, data },
      } = await checkRole(id, companyId, action)
      if (success) {
        if (data.editAccess && data.viewAccess && !data.isPending) {
          if (action === 'RENAME') showRenameShowModal(true)
          if (action === 'DELETE') setOpenDeleteConfirmationModal(true)
        }
        if (data.isPending) setFileStatusModal(true)
        if (data.unAuthorized) setShowAccessModal(true)
        if (data.isRejected) toast.error('Your request has been rejected')
      }
    } catch (err) {
    } finally {
      setRoleCheckValidatorLoader(false)
    }
  }
  useEffect(() => {
    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside)

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])
  const handleDeleteFile = async () => {
    console.log(companyId, 'pp[[')

    if (!companyId) return
    console.log('heelo g')

    try {
      setDeleteLoading(true)
      if (recordId) {
        const {
          data: { success, errors },
        } = await deleteFile(recordId, companyId)
        if (success) {
          // Update the folder structure locally
          toast.success(`File deleted successfully`)
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      }
    } catch (err) {
      console.log(err)
      toast.error('Failed to delete file')
    } finally {
      setDeleteLoading(false)
      setOpenDeleteConfirmationModal(false)
      setRecordId('')
      fetchOverviewData()
    }
  }
  useEffect(() => {
    fetchOverviewData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOverviewData()
  }, [pagination.currentPage, pagination.pageSize]) // eslint-disable-line react-hooks/exhaustive-deps
  const fetchOverviewData = async () => {
    if (!companyId) return
    setRecentUpload(true)
    try {
      const {
        data: { success, data },
      } = await getRecentUploadApi(
        companyId,
        'recentUploadFiles',
        pagination.currentPage,
        pagination.pageSize
      )
      if (success) {
        setShareholders(data?.data?.recordKeeperFiles)
        setPagination((prev) => ({
          ...prev,
          currentPage: data.pagination?.currentPage,
          pageSize: data.pagination?.pageSize,
          totalCount: data.pagination?.totalRecords,
          nextPage: data.pagination?.nextPage,
          prevPage: data.pagination?.prevPage,
          totalPages: data.pagination?.totalPages,
        }))
      }
    } catch (error) {
      console.error(error)
    } finally {
      setRecentUpload(false)
    }
  }
  useEffect(() => {
    if (renameUpdate) {
      fetchOverviewData()
      setRenameUpdate(false)
    }
  }, [renameUpdate]) // eslint-disable-line react-hooks/exhaustive-deps
  const multiFieldGlobalFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const nameMatch = rankItem(`${row.original.documentName}`, value)
    // Determine which match to use based on rank comparison or other logic
    let bestMatch: RankingInfo | undefined
    if (nameMatch.passed) {
      bestMatch = nameMatch
    }

    // Only call addMeta if a best match is found
    if (bestMatch) {
      addMeta({ itemRank: bestMatch })
    }

    // Return whether the search term passed in either field
    return !!bestMatch
  }
  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'documentName',
        id: 'documentName',
        header: 'Name',
        cell: (info) => {
          return (
            <div className='d-flex align-items-center gap-2'>
              <img
                src={getFileType(info.row.original.recordKeeperFileVersion.fileName)}
                alt={info.row.original.documentName}
                width={24}
                height={24}
                className='mb-md-0 mb-2'
              />
              <div>
                <p className='font-size-13 m-0'>{info.row.original.documentName}</p>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'uploadedOn',
        id: 'uploadedOn',
        header: 'Uploaded On',
        cell: (info) => {
          const rawDate = info.row.original.createdAt
          const formattedDate = `${getDateValue(rawDate)} ${getTimeZoneValue(rawDate)}`
          return <span>{formattedDate}</span>
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'filePath',
        id: 'filePath',
        header: 'File Path',
        cell: (info) => {
          console.log(info.row.original)

          const folderPath = `${info.row.original.folderStructure?.structureClassificationLevelOne?.name}/${info.row.original.folderStructure?.structureClassificationLevelTwo?.name}/${info.row.original.folderStructure?.structureClassificationLevelThree?.name}`
          return (
            <div className='d-flex align-items-center gap-2 w-100'>
              {info.row.original.recordKeeperFileVersion.isFileProcessed ? (
                <div className='d-flex align-items-center gap-2 w-100'>
                  <div className='d-flex flex-row w-100 justify-content-between align-items-start'>
                    <div className='d-flex'>
                      {' '}
                      <img
                        src={activeFolderIcon}
                        alt='folder icon'
                        height={20}
                        width={20}
                        className='me-2'
                      />
                      <p className='w-100 font-size-14 text-wrap mb-4 mb-md-0'>
                        {folderPath.length > 150
                          ? `${folderPath.replaceAll('/', ' > ').substring(0, 150)} ....`
                          : folderPath.replaceAll('/', ' > ')}
                      </p>
                    </div>
                    <div
                      className='p-2 cursor-pointer bg-light rounded-2 d-flex justify-content-center  align-items-center'
                      role='button'
                      onClick={() => {
                        const fileUrl = folderPath.split('/')
                        const encodedPath = fileUrl.map(encodeURIComponent).join('/')
                        navigate(`/rk/records-and-files/${encodedPath}`)
                      }}
                    >
                      <i className='fa-solid fa-arrow-up-right-from-square ms-1 cursor-pointer'></i>
                    </div>
                  </div>
                </div>
              ) : (
                <p className='font-size-14 mb-4 mb-md-0'>
                  {formatMessage({
                    id: 'Please wait for 1-2 minutes while the file is processed with AI. After that you can check the "Records & Files" section or "Overview" section',
                  })}
                </p>
              )}
            </div>
          )
        },
        filterFn: 'includesString',
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowKebabMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const table = useReactTable({
    data: shareholders,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    manualPagination: true,
    rowCount: pagination.totalCount,
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: multiFieldGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })
  return (
    <>
      <PageTitle
        breadcrumbs={RecordsKeeperAIOverviewBreadCrumbs}
        description={formatMessage({ id: 'Recent Uploads' })}
      >
        {formatMessage({ id: 'RecordsKeeper AI' })}
      </PageTitle>
      <div className='d-flex flex-md-row flex-column justify-content-between align-items-md-center gap-2 mb-4'>
        <h1 className='font-size-16 mb-0'>{formatMessage({ id: 'Recently Uploaded Files' })}</h1>
        <div className='d-flex gap-md-4 gap-2'>
          <form className='align-items-center w-100 w-md-auto d-flex'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => {
                setGlobalFilter(String(value))
                if (String(value).length > 0) {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: GTMEvent.AdvisorMentorSearchSuccessful,
                      action: 'normal',
                      // metadata: {
                      //     company_id: companyId,
                      //     user_id: currentUser?.userId,
                      //     email: currentUser?.email,
                      //     search_value: value,
                      // },
                    },
                  })
                }
              }}
              className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400'
              placeholder='Search'
            />
          </form>
          {/* <div className='h-100 border bg-white d-flex rounded'>
                        <button
                            className={clsx('text-clr88 btn btn-sm btn-flex fw-semibold py-2 font-size-12 height-36')}
                            id='fc-toogle'
                            title='Sort and Filter'
                            data-bs-toggle='tooltip'
                            data-bs-placement='left'
                            data-bs-dismiss='click'
                            data-bs-trigger='hover'
                            onClick={() => {
                                setRecentUpload(true)
                            }}
                        >
                            <KTSVG
                                path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                                className='svg-icon-6 svg-icon-muted me-1'
                            />
                            <span className='d-none d-md-block'>{formatMessage({ id: 'Filter' })}</span>
                            {/* {selectedShareholderTypeFilter || selectedShareholderSortTypeFilter ? '*' : ''} */}
          {/* </button>
                    </div> */}
        </div>
      </div>
      <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
        {!recentUpload && (shareholders?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage />
          </div>
        ) : (
          <Table className='w-100 mb-0' style={{ minWidth: '600px' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className='min-w-120px pb-6 pt-0 px-4'
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className='min-w-120px pb-6 pt-4'>{formatMessage({ id: 'Actions' })}</th>
                </tr>
              ))}
            </thead>
            <tbody>
              {recentUpload ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                <>
                  {table.getRowModel().rows.map((row, index) => (
                    <React.Fragment key={row.id}>
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell, cellIndex) => (
                          <>
                            {cellIndex === 0 ? (
                              <td
                                key={cell.id}
                                className='pt-5 px-4 border-0'
                                style={{ minWidth: '120px' }}
                              >
                                <div className='d-flex gap-2'>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                              </td>
                            ) : cellIndex === 2 ? (
                              <td
                                key={cell.id}
                                className='pt-5 px-4 border-0'
                                style={{ minWidth: '400px' }}
                              >
                                <div className='d-flex gap-2'>
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                              </td>
                            ) : (
                              <td
                                key={cell.id}
                                className='pt-5 px-4 border-0'
                                style={{ minWidth: '120px' }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            )}
                          </>
                        ))}
                        {row.original.recordKeeperFileVersion.isFileProcessed && (
                          <td className='pt-5 px-4' onClick={(e) => e.stopPropagation()}>
                            <div className='dropdown-container d-flex justify-content-center align-items-center pb-1'>
                              <Dropdown
                                show={index === selectedIndex}
                                onToggle={(isOpen) => {
                                  setSelectedIndex(isOpen ? row.index : undefined)
                                }}
                              >
                                <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                                  <img src={threeDots} alt='' className='three-dots-image' />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className='m-2'>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={() => {
                                      createAccessLog(row.original.recordId)
                                      setFileName(row.original.recordKeeperFileVersion.fileName)
                                      handleViewFile(
                                        row.original.recordKeeperFileVersion.fileVersionId,
                                        row.original.recordId
                                      )
                                    }}
                                  >
                                    <p className='mb-0'>{formatMessage({ id: 'Open' })}</p>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={() => {
                                      createAccessLog(row.original.recordId)
                                      navigate(
                                        '/rk/records-and-files/files-details/' +
                                        row.original.recordId
                                      )
                                      createActivity(row.original.recordId)
                                    }}
                                  >
                                    <p className='mb-0'>{formatMessage({ id: 'Details' })}</p>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={() => {
                                      checkRoleValidate(row.original.recordId, 'RENAME')
                                      setEmail(row.original.createdUser.email)
                                      setAction('RENAME')
                                      setVersionId(
                                        row.original.recordKeeperFileVersion.fileVersionId
                                      )
                                      setRecordId(row.original.recordId)
                                      setFolderOrFileName(row.original.documentName)
                                    }}
                                  >
                                    {roleCheckValidatorLoader && action === 'RENAME' ? (
                                      <span style={{ color: '#50CD89', fontWeight: '400' }}>
                                        Loading...
                                      </span>
                                    ) : (
                                      formatMessage({ id: 'Rename' })
                                    )}
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={() => {
                                      setRecordId(row.original.recordId)
                                      setCreateLinkDrawer(true)
                                    }}
                                  >
                                    <p className='mb-0'>{formatMessage({ id: 'Share' })}</p>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className='text-danger ps-5'
                                    onClick={() => {
                                      setEmail(row.original.createdUser.email)
                                      checkRoleValidate(row.original.recordId, 'DELETE')
                                      setAction('DELETE')
                                      setRecordId(row.original.recordId)
                                    }}
                                  >
                                    {formatMessage({ id: 'Delete' })}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </td>
                        )}
                      </tr>
                    </React.Fragment>
                  ))}
                </>
              )}
            </tbody>
          </Table>
        )}
      </div>
      {!recentUpload && shareholders?.length > 0 && (
        <div className='w-100 d-md-flex justify-content-between align-items-center px-0 mt-8'>
          <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4'>
            <select
              aria-label='Default select example'
              className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13'
              value={pagination.pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value)
                setPagination((prev) => ({
                  ...prev,
                  pageSize: newSize,
                  pageIndex: 0, // Reset to first page when page size changes
                }))
              }}
            >
              {[10, 20, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>

            <span className='font-size-13 text-center text-md-start'>
              {formatMessage({
                id: `Showing ${pagination.currentPage === 1
                  ? 1
                  : (pagination.currentPage - 1) * pagination.pageSize + 1
                  } to ${pagination.totalCount > pagination.currentPage * pagination.pageSize
                    ? pagination.currentPage * pagination.pageSize
                    : pagination.totalCount -
                    pagination.currentPage * pagination.pageSize +
                    pagination.currentPage * pagination.pageSize
                  } of ${pagination.totalCount} records`,
              })}
            </span>
          </div>

          {/* <Pagination className='w-md-100 w-auto d-flex flex-md-end justify-content-center justify-content-md-end '>
                        <Pagination.First
                            onClick={() => handlePageChange(1)}
                            disabled={pagination.prevPage === null}
                        >
                            {'<<'}
                        </Pagination.First>

                        <Pagination.Prev
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.prevPage === null}
                        >
                            {'<'}
                        </Pagination.Prev>

                        {Array.from({ length: 3 }, (_, i) => {
                            const pageNumber = pagination.currentPage + i;
                            return (
                                pageNumber <= pagination.totalPages && (
                                    <Pagination.Item
                                        key={pageNumber}
                                        active={pageNumber === pagination.currentPage}
                                        onClick={() => handlePageChange(pageNumber)}
                                    >
                                        {pageNumber}
                                    </Pagination.Item>
                                )
                            );
                        })}

                        <Pagination.Next
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={!pagination.nextPage}
                        >
                            {'>'}
                        </Pagination.Next>

                        <Pagination.Last
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={!pagination.nextPage}
                        >
                            {'>>'}
                        </Pagination.Last>
                    </Pagination> */}
          <Pagination className='w-md-100 w-auto d-flex flex-md-end justify-content-center justify-content-md-end'>
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={pagination.prevPage === null}
            >
              {'<<'}
            </Pagination.First>

            <Pagination.Prev
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.prevPage === null}
            >
              {'<'}
            </Pagination.Prev>

            {(() => {
              const items = []
              const totalPages = pagination.totalPages
              const current = pagination.currentPage
              const pageNeighbours = 1 // pages around current

              const startPage = Math.max(2, current - pageNeighbours)
              const endPage = Math.min(totalPages - 1, current + pageNeighbours)

              // Always show first page
              items.push(
                <Pagination.Item key={1} active={current === 1} onClick={() => handlePageChange(1)}>
                  1
                </Pagination.Item>
              )

              // Show start ellipsis
              if (startPage > 2) {
                items.push(<Pagination.Ellipsis key='start-ellipsis' disabled />)
              }

              // Middle pages
              for (let i = startPage; i <= endPage; i++) {
                items.push(
                  <Pagination.Item
                    key={i}
                    active={i === current}
                    onClick={() => handlePageChange(i)}
                  >
                    {i}
                  </Pagination.Item>
                )
              }

              // Show end ellipsis
              if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key='end-ellipsis' disabled />)
              }

              // Always show last page
              if (totalPages > 1) {
                items.push(
                  <Pagination.Item
                    key={totalPages}
                    active={current === totalPages}
                    onClick={() => handlePageChange(totalPages)}
                  >
                    {totalPages}
                  </Pagination.Item>
                )
              }

              return items
            })()}

            <Pagination.Next
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.nextPage}
            >
              {'>'}
            </Pagination.Next>

            <Pagination.Last
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={!pagination.nextPage}
            >
              {'>>'}
            </Pagination.Last>
          </Pagination>
        </div>
      )}
      <RenameFileOrFolderModal
        showRenameModal={showRenameModal}
        showRenameShowModal={showRenameShowModal}
        type={''}
        versionId={versionId}
        recordId={recordId}
        folderOrFileName={folderOrFileName}
        folderStructure={''}
        setFolderStructure={() => { }}
        setSelectedFolders={() => { }}
        setLocalExpanded={() => { }}
        setSelectedPath={() => { }}
        setRenameUpdate={setRenameUpdate}
      />
      <ViewDocModal
        isActive={isActive}
        setIsActive={setIsActive}
        fileName={fileName}
        isRecordKeeper={true}
        isLoading={isLoading}
        viewableData={viewableData}
      />
      <RecordKeeperCreateLinkDrawer
        recordId={recordId}
        createLinkDrawer={isOpenShareDrawer}
        setCreateLinkDrawer={setIsOpenShareDrawer}
        setCreateRecordDrawer={setCreateLinkDrawer}
      />
      <RecordShareDrawer
        recordId={recordId}
        createRecordDrawer={createLinkDrawer}
        setCreateRecordDrawer={setCreateLinkDrawer}
        setIsOpenShareDrawer={setIsOpenShareDrawer}
      />
      <DeleteClassificationModal
        setDeleteShowModal={setOpenDeleteConfirmationModal}
        showDeleteModal={openDeleteConfirmationModal}
        handleDelete={handleDeleteFile}
        deleteLoading={deleteLoading}
        headingMessage={'Delete File'}
        warningText={'This will permanently delete the file.'}
        description={`Are you sure you want to delete this File?`}
      />
      <RequestAccessModal
        showAccessModal={showAccessModal}
        setShowAccessModal={setShowAccessModal}
        headingMessage="You don't have access to perform this action"
        description={`Your request will be sent to ${email}.You cannot submit another request for 48 hours after submission`}
        recordId={recordId}
        action={action}
      />
      <RequestFileStatusModal
        showFileStatusModal={showFileStatusModal}
        setFileStatusModal={setFileStatusModal}
        headingMessage='Request is already in process'
        description='Your request is in the queue.Please kindly wait until you recieve a response'
      />
    </>
  )
}
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)
  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce, onChange])
  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}
